import { getAdminUrl, getMergedHeaders, useReapitConnectSession } from '@reapitform/hooks'
import { AdminFieldLink } from '../types'

/**
 * Imperative link/unlink for a form field's recipient-field mapping. The target form-field reference is only
 * known at call time (it may differ between the old and new link in a single edit), so this can't use the
 * fixed-path `useAdminUpdate` hook — mirrors `useBuilderDeletes`'s pattern.
 */
export const useSetFieldLink = () => {
  const session = useReapitConnectSession()

  const setRecipientLink = async (
    formFieldReferenceID: string,
    recipientFieldReferenceID: string | null,
  ): Promise<AdminFieldLink> => {
    const headers = await getMergedHeaders(session)
    if (!headers) throw new Error('Your session has expired — please sign in again.')

    const response = await fetch(getAdminUrl(`/api/form-fields/${formFieldReferenceID}/recipient-link`)(), {
      method: 'PUT',
      headers: { ...headers, 'Content-Type': 'application/json' } as HeadersInit,
      body: JSON.stringify({ recipientFieldReferenceID }),
    })

    if (!response.ok) {
      let message = `Link failed (${response.status})`
      try {
        const body = await response.json()
        message = body?.description || body?.Description || message
      } catch {
        // Non-JSON body — keep the status-based message.
      }
      throw new Error(message)
    }

    return response.json()
  }

  return { setRecipientLink }
}
